import { ValidationErrorResponseSchema } from '@mntn/api-contracts/_shared/error-responses'
import { ErrorResponseSchema } from '@mntn/result'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  DeleteOfferingBodySchema,
  DeleteOfferingRequestSchema,
  DeleteOfferingResponseSchema,
  GenerateAdvertiserIndustryMatchBodySchema,
  GenerateAdvertiserIndustryMatchFailureResponses,
  GenerateAdvertiserIndustryMatchRequestSchema,
  GenerateAdvertiserIndustryMatchResponseSchema,
  GenerateDealInsightsBodySchema,
  GenerateDealInsightsRequestSchema,
  GenerateDealInsightsResponseSchema,
  GenerateTextFailureResponses,
  GetOfferingsQuerySchema,
  GetOfferingsRequestSchema,
  GetOfferingsResponseSchema,
  PostOfferingBodySchema,
  PostOfferingRequestSchema,
  PostOfferingResponseSchema,
  PostOfferingVersionBodySchema,
  PostOfferingVersionRequestSchema,
  PostOfferingVersionResponseSchema,
} from './schemas.ts'

const c = initContract()

const BASE = '/seller/v1/offerings'
const BadRequestResponseSchema = z.union([ValidationErrorResponseSchema, ErrorResponseSchema])

export const offeringsContract = c.router(
  {
    list: c.query({
      method: 'GET',
      path: BASE,
      description: 'Get paginated offerings',
      query: GetOfferingsQuerySchema,
      request: GetOfferingsRequestSchema,
      responses: GetOfferingsResponseSchema,
    }),
    create: c.mutation({
      method: 'POST',
      path: BASE,
      description: 'Create a new offering',
      body: PostOfferingBodySchema,
      request: PostOfferingRequestSchema,
      responses: {
        200: PostOfferingResponseSchema,
        400: BadRequestResponseSchema,
        401: ErrorResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
        422: ErrorResponseSchema,
        500: ErrorResponseSchema,
        502: ErrorResponseSchema,
        503: ErrorResponseSchema,
      },
    }),
    createVersion: c.mutation({
      method: 'POST',
      path: `${BASE}/versions`,
      description: 'Create a new version of an existing offering',
      body: PostOfferingVersionBodySchema,
      request: PostOfferingVersionRequestSchema,
      responses: {
        200: PostOfferingVersionResponseSchema,
        400: BadRequestResponseSchema,
        401: ErrorResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
        422: ErrorResponseSchema,
        500: ErrorResponseSchema,
        502: ErrorResponseSchema,
        503: ErrorResponseSchema,
      },
    }),
    delete: c.mutation({
      method: 'DELETE',
      path: BASE,
      description: 'Delete an offering',
      body: DeleteOfferingBodySchema,
      request: DeleteOfferingRequestSchema,
      responses: {
        200: DeleteOfferingResponseSchema,
        404: ErrorResponseSchema,
        500: ErrorResponseSchema,
      },
    }),
    generateDealInsights: c.mutation({
      method: 'POST',
      path: `${BASE}/generate-deal-insights`,
      description: 'Generate deal sell sheet and demographics from offering form data',
      body: GenerateDealInsightsBodySchema,
      request: GenerateDealInsightsRequestSchema,
      responses: {
        200: GenerateDealInsightsResponseSchema,
        ...GenerateTextFailureResponses,
      },
    }),
    generateAdvertiserIndustryMatch: c.mutation({
      method: 'POST',
      path: `${BASE}/generate-advertiser-industry-match`,
      description: 'Generate advertiser industry fit for a saved offering',
      body: GenerateAdvertiserIndustryMatchBodySchema,
      request: GenerateAdvertiserIndustryMatchRequestSchema,
      responses: {
        200: GenerateAdvertiserIndustryMatchResponseSchema,
        ...GenerateAdvertiserIndustryMatchFailureResponses,
      },
    }),
  },
  {
    strictStatusCodes: true,
    commonResponses: {
      400: BadRequestResponseSchema,
      403: ErrorResponseSchema,
    },
    metadata: { tags: ['Offerings'] },
  },
)

export type OfferingsContract = typeof offeringsContract
